const SCENT_DRAG_THRESHOLD_PX = 40;
const SCENT_DESKTOP_MQ = "(min-width: 990px)";

class ScentQuizSection extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-track]");
    this.viewport = this.querySelector("[data-track-viewport]");
    this.dotsContainer = this.querySelector("[data-dots]");
    this.prevBtn = this.querySelector("[data-prev]");
    this.nextBtn = this.querySelector("[data-next]");

    this.visibleCardsDesktop = Math.max(1, Number(this.dataset.visibleCardsDesktop) || 4);
    this.visibleCardsMobile = Math.max(1, Number(this.dataset.visibleCardsMobile) || 2);
    this.visibleCards = this.visibleCardsDesktop;
    this.cards = [];
    this.dots = [];

    this.autoplay = this.dataset.autoplay === "true";
    this.intervalMs = Number(this.dataset.interval) || 5000;
    this.currentIndex = 0;
    this.timer = null;
    this._drag = null;
    this._didDrag = false;
    this._abort = new AbortController();

    this.refreshCards();
    this.syncVisibleCards();
    this.buildDots();
    this.bindCarousel();
    this.applyCardLinksFromMap();
    this.bindCardLinks();
    this.updateCarousel(false);
    this.startAutoplay();

    const { signal } = this._abort;
    window.addEventListener(
      "resize",
      () => {
        const previousVisible = this.visibleCards;
        this.refreshCards();
        this.syncVisibleCards();

        if (previousVisible !== this.visibleCards) {
          this.currentIndex = Math.min(this.currentIndex, this.getMaxIndex());
          this.buildDots();
        }

        this.updateCarousel(false);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this._abort.abort();
  }

  refreshCards() {
    this.cards = Array.from(this.querySelectorAll("[data-card]"));
  }

  syncVisibleCards() {
    const isDesktop = window.matchMedia(SCENT_DESKTOP_MQ).matches;
    this.visibleCards = isDesktop ? this.visibleCardsDesktop : this.visibleCardsMobile;
  }

  getMaxIndex() {
    if (this.cards.length <= this.visibleCards) return 0;
    return this.cards.length - this.visibleCards;
  }

  buildDots() {
    if (!this.dotsContainer) return;

    const dotCount = this.getMaxIndex() + 1;
    this.dotsContainer.replaceChildren();
    this.dots = [];

    if (dotCount <= 1) {
      this.dotsContainer.hidden = true;
      return;
    }

    this.dotsContainer.hidden = false;

    for (let index = 0; index < dotCount; index += 1) {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = "scent-quiz__dot";
      dot.role = "tab";
      dot.dataset.dot = "";
      dot.dataset.slideIndex = String(index);
      dot.setAttribute("aria-label", `Go to slide ${index + 1}`);
      this.dotsContainer.appendChild(dot);
      this.dots.push(dot);
    }

    this.updateDotStates();
  }

  updateDotStates() {
    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === this.currentIndex;
      dot.classList.toggle("is-active", isActive);
      dot.setAttribute("aria-selected", isActive ? "true" : "false");
    });
  }

  applyCardLinksFromMap() {
    const mapNode = this.querySelector("[data-scent-link-map]");
    if (!mapNode) return;

    let entries = [];
    try {
      entries = JSON.parse(mapNode.textContent || "[]");
    } catch (_error) {
      return;
    }

    const urlByHandle = new Map();
    entries.forEach((entry) => {
      if (!entry?.handle) return;
      const url = this.resolveLinkEntryUrl(entry);
      if (url) urlByHandle.set(entry.handle, url);
    });

    this.querySelectorAll("[data-scent-handle]").forEach((card) => {
      const handle = card.getAttribute("data-scent-handle");
      if (!handle) return;

      const url = urlByHandle.get(handle);
      if (!url) return;

      card.dataset.collectionUrl = url;

      if (card.tagName === "A") {
        card.setAttribute("href", url);
        card.classList.add("scent-quiz__card--linked");
        return;
      }

      card.classList.add("scent-quiz__card--linked");
    });
  }

  resolveLinkEntryUrl(entry) {
    if (entry.url) return entry.url;

    const link = entry.link;
    if (!link) return "";

    if (typeof link === "string") {
      return link.includes("http") || link.startsWith("/") ? link : "";
    }

    if (typeof link.url === "string" && link.url.length) return link.url;
    if (typeof link.text === "string" && (link.text.includes("http") || link.text.startsWith("/"))) {
      return link.text;
    }

    return "";
  }

  bindCardLinks() {
    const { signal } = this._abort;

    this.addEventListener(
      "click",
      (event) => {
        if (this._didDrag) {
          const draggedLink = event.target.closest("a.scent-quiz__card--linked");
          if (draggedLink) event.preventDefault();
          return;
        }

        const card = event.target.closest("[data-card][data-collection-url]");
        if (!card) return;

        const href = card.getAttribute("data-collection-url");
        if (!href) return;

        if (card.tagName === "A") return;

        event.preventDefault();
        window.location.assign(href);
      },
      { signal }
    );
  }

  bindCarousel() {
    if (!this.track) return;

    const { signal } = this._abort;

    this.prevBtn?.addEventListener(
      "click",
      () => {
        this.goTo(this.currentIndex - 1);
        this.restartAutoplay();
      },
      { signal }
    );

    this.nextBtn?.addEventListener(
      "click",
      () => {
        this.goTo(this.currentIndex + 1);
        this.restartAutoplay();
      },
      { signal }
    );

    this.dotsContainer?.addEventListener(
      "click",
      (event) => {
        const dot = event.target.closest("[data-dot]");
        if (!dot) return;
        this.goTo(Number(dot.dataset.slideIndex) || 0);
        this.restartAutoplay();
      },
      { signal }
    );

    this.addEventListener("mouseenter", () => this.stopAutoplay(), { signal });
    this.addEventListener("focusin", () => this.stopAutoplay(), { signal });
    this.addEventListener("mouseleave", () => this.startAutoplay(), { signal });
    this.addEventListener("focusout", () => this.startAutoplay(), { signal });

    if (!this.viewport) return;

    const onPointerDown = (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (event.target.closest(".scent-quiz__controls, .scent-quiz__controls *")) return;

      this._didDrag = false;
      this._drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
      };

      if (this.cards.length > this.visibleCards) {
        this.viewport.setPointerCapture(event.pointerId);
      }
      this.stopAutoplay();
    };

    const onPointerMove = (event) => {
      if (!this._drag || event.pointerId !== this._drag.pointerId) return;

      const deltaX = event.clientX - this._drag.startX;
      const deltaY = event.clientY - this._drag.startY;

      if (Math.abs(deltaX) > SCENT_DRAG_THRESHOLD_PX && Math.abs(deltaX) > Math.abs(deltaY)) {
        this._didDrag = true;
      }
    };

    const onPointerEnd = (event) => {
      if (!this._drag || event.pointerId !== this._drag.pointerId) return;

      const deltaX = event.clientX - this._drag.startX;

      try {
        this.viewport.releasePointerCapture(event.pointerId);
      } catch (_error) {
        /* already released */
      }

      const wasDrag = this._didDrag && Math.abs(deltaX) >= SCENT_DRAG_THRESHOLD_PX;
      this._drag = null;

      if (wasDrag) {
        if (deltaX < 0) this.goTo(this.currentIndex + 1);
        else this.goTo(this.currentIndex - 1);
        this.restartAutoplay();
        window.setTimeout(() => {
          this._didDrag = false;
        }, 0);
        return;
      }

      this._didDrag = false;
      this.startAutoplay();
    };

    this.viewport.addEventListener("pointerdown", onPointerDown, { signal });
    this.viewport.addEventListener("pointermove", onPointerMove, { signal, passive: true });
    this.viewport.addEventListener("pointerup", onPointerEnd, { signal });
    this.viewport.addEventListener("pointercancel", onPointerEnd, { signal });
  }

  goTo(index) {
    this.currentIndex = Math.min(Math.max(index, 0), this.getMaxIndex());
    this.updateCarousel(true);
  }

  updateCarousel(animate) {
    if (!this.track || !this.cards.length || this.cards.length <= this.visibleCards) {
      if (this.track) {
        this.track.style.transform = "";
        this.track.style.transition = "";
      }
      this.updateDotStates();
      return;
    }

    const card = this.cards[0];
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const offset = (card.offsetWidth + gap) * this.currentIndex;

    this.track.style.transition = animate ? "transform 350ms ease" : "none";
    this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    this.updateDotStates();
  }

  startAutoplay() {
    if (!this.autoplay || this.getMaxIndex() === 0 || this.timer) return;

    this.timer = window.setInterval(() => {
      const next = this.currentIndex >= this.getMaxIndex() ? 0 : this.currentIndex + 1;
      this.goTo(next);
    }, this.intervalMs);
  }

  stopAutoplay() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

if (!customElements.get("scent-quiz-section")) {
  customElements.define("scent-quiz-section", ScentQuizSection);
}
